import {
  Box,
  Button,
  Paper,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  stepConnectorClasses,
  useTheme,
  Grid2,
  Typography,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useState, ReactNode, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  ConfigureRelatorio,
  ConfigureHosts,
} from "../../../../componentes/easyReport/stepsNovoRelatorio";
import { styled } from "@mui/material/styles";
import { ArrowBack } from "@mui/icons-material";
import { AgendamentoConfig } from "../../../../componentes/easyReport/stepsNovoRelatorio/configureAgendamento";
import { PdfPreview } from "../../../../componentes/easyReport/pdfPreview/pdfPreview";
import * as JSON5 from "json5";
import * as dayjs from "dayjs";
import api from "../../../../services/api";
import { notifyOnPage } from "../../../../utils/notifyOnPage";
interface StepType {
  label: string;
  description: ReactNode;
}

export interface AgendamentoDataType {
  dataInicial: Date | null;
  tipoColeta: string;
  ocorrencias: number | null;
  intervaloColeta: number | null;
}

enum IntervaloColeta {
  Dias = "day",
  Semanas = "week",
  Mêses = "month",
}
interface OutletContextType {
  config: EasyReportConfig[];
  setConfig: React.Dispatch<React.SetStateAction<EasyReportConfig[]>>;
}
export default function NovoAgendamento() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [relatorioData, setRelatorioData] = useState<RelatorioDataType>({
    title: "",
    perfil: [],
    hideGraphs: false,
  });
  const [hostData, setHostData] = useState<HostDataType[]>([]);
  const [agendamentoData, setAgendamentoData] = useState<AgendamentoDataType>({
    dataInicial: null,
    tipoColeta: "",
    ocorrencias: null,
    intervaloColeta: null,
  });
  const [perfil, setPerfil] = useState<PerfilDataType[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(false);
  const [variaveisPreview, setVariaveisPreview] = useState<Variaveis[][]>([]);
  const { config } = useOutletContext<OutletContextType>();

  const monthLimit = useMemo(() => {
    return config.find((c) => c.nome === "geral")?.configuracoes?.interval;
  }, [config]);

  useEffect(() => {
    const profileHosts = relatorioData.perfil.map((p) => ({
      perfilId: p.id,
      perfilTitle: p.titulo,
      variaveis: p.variaveis?.map((v) => ({
        id: v.id,
        titulo: v.title,
        item: v.keyName,
        hosts: [{ host: "", item: [] }],
        multiple: v.multiple,
      })),
    }));
    setHostData(profileHosts);
  }, [relatorioData.perfil]);

  const steps: StepType[] = [
    {
      label: "Configure o Relatório",
      description: (
        <ConfigureRelatorio
          relatorioData={relatorioData}
          setRelatorioData={setRelatorioData}
          setPerfil={setPerfil}
          setVariaveisPreview={setVariaveisPreview}
        />
      ),
    },
    {
      label: "Configure os Hosts",
      description: (
        <ConfigureHosts hostData={hostData} setHostData={setHostData} />
      ),
    },
    {
      label: "Agendamento",
      description: (
        <AgendamentoConfig
          agendamentoData={agendamentoData}
          setAgendamentoData={setAgendamentoData}
        />
      ),
    },
  ];

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      try {
        setLoading(true);
        const logsString = localStorage.getItem("logs");
        const logsLoginClienteId = logsString
          ? JSON.parse(logsString)?.id
          : "";
        await api.put("/easyReport/v2/createSchedule", {
          titulo: relatorioData.title,
          profiles: relatorioData.perfil.map((perfil, idx) => ({
            id: perfil.id,
            titulo: perfil.titulo,
            variaveis: (hostData[idx]?.variaveis ?? []).map((variavel) => ({
              id: variavel.id,
              titulo: variavel.titulo,
              item: variavel.item,
              hosts: variavel.hosts.map((h) => ({
                hostId: h.host,
                itemId: h.item,
              })),
              multiple: variavel.multiple,
            })),
            code: perfil.code,
            view: perfil.view,
            styles: perfil.styles,
            hasDefaultFunction: perfil.hasDefaultFunction,
          })),
          ocultarGraficos: relatorioData.hideGraphs,
          dataInicial: agendamentoData.dataInicial?.toISOString(),
          tipoColeta: agendamentoData.tipoColeta,
          intervaloColeta: agendamentoData.intervaloColeta,
          quantidadeOcorrencias: agendamentoData.ocorrencias,
          logsLoginClienteId,
        });
      } catch (error) {
        console.error("Erro ao agendar relatório:", error);

        notifyOnPage(
          window.location.pathname,
          (error as any)?.response?.data?.error ||
            "Erro ao realizar agendamento.",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
        navigate(`/easyReportV2/agendamento`);
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 10,
      left: "calc(-50% + 16px)",
      right: "calc(50% + 16px)",
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.success.main,
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.success.main,
      },
    },
  }));

  const verifyStep = () => {
    if (activeStep === 0) {
      return !relatorioData.title.trim() || relatorioData.perfil.length === 0;
    }
    if (activeStep === 1) {
      return verifyHosts();
    }
    if (activeStep === 2) {
      const today = dayjs();

      const month = today.diff(agendamentoData.dataInicial, "month", true);

      return (
        !agendamentoData.dataInicial ||
        month > monthLimit + 1 ||
        !agendamentoData.intervaloColeta ||
        !agendamentoData.ocorrencias ||
        !agendamentoData.tipoColeta
      );
    }
  };

  const verifyHosts = () => {
    const hasEmptyHosts = hostData.some((perfil) =>
      perfil.variaveis?.some(
        (variavel) =>
          !variavel.hosts.length ||
          variavel.hosts.some((h) => !h.host.trim() || h.item.length === 0),
      ),
    );
    return hasEmptyHosts;
  };

  const styles = {
    container: {
      width: "100%",
      maxWidth: "100%",
      height: "85vh",
    },
    headerContainer: {
      display: "flex",
      alignItems: "center",
      justifyContents: "center",
      gap: 1,
      mb: 1,
    },
    paper: {
      padding: 3,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: "100%",
      height: { xs: "auto" },
      maxHeight: { xs: "none", md: "85vh" },
      overflowY: { xs: "visible", md: "auto" },
    },
    boxContent: {
      maxHeight: { xs: "none", md: "67vh" },
      overflowY: { xs: "visible", md: "auto" },
    },
    boxHeader: {
      display: "flex",
      height: "fit-content",
      justifyContent: "space-between",
      alignItems: "center",
    },
    button: {
      height: "40px",
    },
    boxReportViwer: {
      display: "flex",
      height: "85vh",
      gap: 2,
      width: { xs: "100%", md: "100%" },
      overflow: "hidden",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.paper,
      borderRadius: 1,
    },
    boxButtonStep: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "flex-end",
      gap: 1,
    },
  };

  const variableNames = useMemo(
    () =>
      variaveisPreview.map((p) =>
        p?.map((v: any) => ({
          nome: v.variableName,
          itens: v.keyName.map((item: any) => item.key),
          multiple: v.multiple,
        })),
      ),
    [variaveisPreview, perfil],
  );

  const previewData = useMemo(() => {
    if (perfil.length === 0) return { view: [], code: [], styles: [] };

    const processedPerfil = perfil.map((p) => {
      const newViews: string[] = [];
      const newStylesArr: Record<string, any>[] = [];
      for (let i = 0; i < p.styles.length; i++) {
        let newView = p.view[i];
        let newStyles: { [key: string]: any } = {};
        try {
          const stylesObj = JSON5.parse(p.styles[i]);
          Object.keys(stylesObj).forEach((styleKey) => {
            const newKey = `${styleKey}_${p.id}_${i}`;
            const regex = new RegExp(`\\b${styleKey}\\b`, "g");
            newView = newView.replace(regex, newKey);
            newStyles[newKey] = stylesObj[styleKey];
          });
        } catch (e) {
          newStyles = {};
        }
        newViews.push(newView);
        newStylesArr.push(newStyles);
      }
      return {
        ...p,
        view: newViews,
        styles: newStylesArr,
      };
    });

    return processedPerfil.reduce(
      (acc: { view: any[]; code: any[]; styles: any[] }, p) => {
        acc.view.push(...(p.view || []));
        acc.code.push(...(p.code || []));
        acc.styles.push(...(p.styles || []));
        return acc;
      },
      { view: [], code: [], styles: [] },
    );
  }, [perfil]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <IconButton onClick={() => navigate("/easyReportV2/agendamento")}>
          <ArrowBack />
        </IconButton>

        <Typography variant="h5">Novo Agendamento</Typography>
      </Box>
      <Grid2 container spacing={4} sx={styles.container}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={styles.paper}>
            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              connector={<QontoConnector />}
            >
              {steps.map(({ label }) => (
                <Step key={label}>
                  <StepLabel>{isMobile ? "" : label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={styles.boxContent}>{steps[activeStep].description}</Box>
            <Box sx={styles.boxButtonStep}>
              <Button
                color="secondary"
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                {"Voltar"}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  color: theme.palette.text.invert,
                  backgroundColor: theme.palette.secondary.main,
                }}
                disabled={verifyStep()}
              >
                {activeStep === steps.length - 1
                  ? loading
                    ? "Agendando..."
                    : "Agendar Relatório"
                  : "Continuar"}
              </Button>
            </Box>
          </Paper>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ height: "80vh" }}>
          <Box sx={styles.boxReportViwer}>
            <PdfPreview
              variableNames={variableNames}
              code={previewData.code}
              rawContent={previewData.view}
              styles={previewData.styles}
              verifyContent={true}
              watermark={true}
            />
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
}
